import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Label, Input, Spinner, Button, Alert } from "reactstrap";
import { useParams } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import PortalCustomizationForm from "components/Company/PortalCustomizationForm";
import {
  getCustomPages,
  getRole3SidebarItems,
  mergePortalConfig,
} from "helpers/companyPortalConfig";
import { pruneDocumentIds } from "helpers/customPageDocuments";
import { documents } from "helpers/backend-helper";
import {
  getCompany,
  createCompany,
  updateCompany,
  deleteCompany,
  clearCompanyErrors,
  getCompanies,
} from "store/actions";
import { getCsrfHeaders } from "helpers/api_helper";

const MAX_LOGO_MB = 2;
const API_BASE = process.env.REACT_APP_API_BASE_URL;
// const API_BASE = "http://localhost:5000/api";
// const API_BASE = "http://192.168.100.4:5000/api";

const IMAGE_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";
const MAX_PORTAL_CUSTOM_HTML_LEN = 1048576;

const Company = (props) => {
  const { id: companyId } = useParams();
  const isEdit = !!companyId;
  const dispatch = useDispatch();
  const { user: loggedUser } = useSelector((state) => state.Login || {});
  const isSuperadmin = Number(loggedUser?.role) === 1;

  const {
    createCompanyLoading: creating,
    updateCompanyLoading: updating,
    deleteCompanyLoading: deleting,
    message,
    selectedCompany,
    getCompanyLoading,
  } = useSelector((state) => state.Company || {});

  const themeColor = useSelector((state) => state.theme.color);
  const companyData = selectedCompany?.company;
  const isPublicCompany = !!companyData?.isPublic;
  const isDistributorCompany = !!companyData?.isDistributor;
  const isSpecialCompany = isPublicCompany || isDistributorCompany;

  useEffect(() => {
    document.documentElement.style.setProperty("--scrollbar-color", themeColor);
  }, [themeColor]);

  useEffect(() => {
    if (isEdit) dispatch(getCompany(companyId));
  }, [dispatch, isEdit, companyId]);

  useEffect(() => {
    if (!isEdit || !companyId || !isSuperadmin) {
      setCompanyDocuments([]);
      setCompanyDocumentsReady(false);
      setCompanyDocumentsError("");
      return undefined;
    }
    let cancelled = false;
    setCompanyDocumentsReady(false);
    setCompanyDocumentsError("");
    Promise.resolve()
      .then(() => documents({ companyId, all: true }))
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        setCompanyDocuments(items);
        setCompanyDocumentsReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setCompanyDocumentsError(err?.response?.data?.message || err?.message || "Failed to load documents");
        setCompanyDocumentsReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, companyId, isSuperadmin]);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => dispatch(clearCompanyErrors()), 8000);
      return () => clearTimeout(t);
    }
  }, [message, dispatch]);

  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#06427a");
  const [dashboardConfig, setDashboardConfig] = useState(() => mergePortalConfig(null));
  const [sidebarConfig, setSidebarConfig] = useState(() => ({
    items: getRole3SidebarItems(null),
  }));

  const [portalCustomHtmlEnabled, setPortalCustomHtmlEnabled] = useState(false);
  const [portalCustomHtml, setPortalCustomHtml] = useState("");
  const [customPages, setCustomPages] = useState([]);
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [companyDocumentsReady, setCompanyDocumentsReady] = useState(false);
  const [companyDocumentsError, setCompanyDocumentsError] = useState("");

  const [logo, setLogo] = useState(null);        
  const [logoPreview, setLogoPreview] = useState(null); 
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (isEdit && companyData) {
      setDomain(companyData.domain || "");
      setName(companyData.name || "");
      setPrimaryColor(companyData.primaryColor || "#06427a");
      setLogoPreview(companyData.logoUrl || null);
      setDashboardConfig(mergePortalConfig(companyData.dashboardConfig || null));
      setSidebarConfig({
        items: getRole3SidebarItems(companyData.sidebarConfig || null).map((item) => ({ ...item })),
      });
      setPortalCustomHtmlEnabled(!!companyData.portalCustomHtmlEnabled);
      setPortalCustomHtml(
        companyData.portalCustomHtml != null ? String(companyData.portalCustomHtml) : ""
      );
      setCustomPages(
        getCustomPages(companyData.customPages || null).map((page) => ({
          ...page,
          documentIds: pruneDocumentIds(page.documentIds, companyDocuments, {
            ready: companyDocumentsReady,
          }),
        }))
      );
    } else if (!isEdit) {
      setDomain("");
      setName("");
      setPrimaryColor("#06427a");
      setLogo(null);
      setLogoPreview(null);
      setDashboardConfig(mergePortalConfig(null));
      setSidebarConfig({
        items: getRole3SidebarItems(null).map((item) => ({ ...item })),
      });
      setPortalCustomHtmlEnabled(false);
      setPortalCustomHtml("");
      setCustomPages([]);
    }
    // companyDocuments / companyDocumentsReady are read in customPages mapping but
    // intentionally omitted from deps so a later document fetch does not reset
    // name, domain, HTML, and other hydrated fields.
  }, [companyData, isEdit]);

  useEffect(() => {
    if (!isEdit || !companyDocumentsReady) return;
    setCustomPages((prev) =>
      prev.map((page) => ({
        ...page,
        documentIds: pruneDocumentIds(page.documentIds, companyDocuments, { ready: true }),
      }))
    );
  }, [isEdit, companyDocumentsReady, companyDocuments]);

  const onLogoChange = (e) => {
    setUploadError("");
    const file = e.target.files?.[0];
    if (!file) return;

    if (!/image\/(png|jpe?g|svg\+xml|webp)/i.test(file.type)) {
      alert("Invalid file type");
      return;
    }
    if (file.size > MAX_LOGO_MB * 1024 * 1024) {
      alert(`File too large (>${MAX_LOGO_MB}MB)`);
      return;
    }

    if (logoPreview && logoPreview.startsWith("blob:")) {
      try { URL.revokeObjectURL(logoPreview); } catch { }
    }

    setLogo(file);
    setLogoPreview(URL.createObjectURL(file)); 
  };

  const fetchWithTimeout = (url, opts = {}, ms = 20000) => {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), ms);
    return fetch(url, { ...opts, signal: controller.signal }).finally(() => clearTimeout(t));
  };

  const uploadViaApi = async (file, access = "public") => {
    if (!isEdit && !domain.trim()) {
      throw new Error("Enter a domain before uploading portal images.");
    }

    const fd = new FormData();
    fd.append("file", file);
    if (isEdit) fd.append("companyId", companyId);
    else fd.append("domain", domain);
    fd.append("access", access || "public");

    const resp = await fetchWithTimeout(`${API_BASE}/uploads/direct`, {
      method: "POST",
      credentials: "include",
      headers: getCsrfHeaders(),
      body: fd,
    }, 30000);

    const out = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(out?.message || "Upload failed");
    }
    if (!out?.publicUrl) {
      throw new Error("Upload succeeded but no publicUrl returned");
    }
    return {
      publicUrl: out.publicUrl,
      key: out.key,
      logoUrl: out.publicUrl,
      logoKey: out.key,
    };
  };

  const onSubmit = async () => {
    if (!domain.trim()) return alert("Domain is required");
    if (!name.trim()) return alert("Company name is required");
    if (!isEdit && !logo) return alert("Logo is required");

    try {
      setUploadError("");
      setUploadingLogo(true);

      let logoUrl = logoPreview || null;
      let logoKey = undefined;

      if (logo) {
        const uploaded = await uploadViaApi(logo);
        logoUrl = uploaded.logoUrl;
        logoKey = uploaded.logoKey;
        setLogoPreview(uploaded.logoUrl); 
      }

      const basePayload = {
        ...(isSpecialCompany ? {} : { domain }),
        name,
        primaryColor,
        dashboardConfig,
        sidebarConfig,
        ...(logoUrl ? { logoUrl } : {}),
        ...(logoKey ? { logoKey } : {}),
      };

      if (isSuperadmin && portalCustomHtml.length > MAX_PORTAL_CUSTOM_HTML_LEN) {
        alert(`Custom HTML must be at most ${MAX_PORTAL_CUSTOM_HTML_LEN} characters.`);
        return;
      }

      const editPayload = isSuperadmin
        ? { ...basePayload, portalCustomHtmlEnabled, portalCustomHtml, customPages }
        : basePayload;

      dispatch(
        isEdit
          ? updateCompany(editPayload, props.router.navigate, getCompanies, companyId)
          : createCompany(basePayload, props.router.navigate, getCompanies)
      );
    } catch (err) {
      console.error(err);
      setUploadError(err?.message || "Logo upload failed. Please try again.");
    } finally {
      setUploadingLogo(false);
    }
  };

  document.title = `${isEdit ? "Update" : "Create"} Company - ColdBlock Technologies Inc.`;

  if (isEdit && getCompanyLoading) {
    return (
      <div
        className="loading-container"
        style={{ width: "100%", height: "60dvh", display: "grid", placeItems: "center" }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content white-bg">
        {!getCompanyLoading ? (
          <Container fluid>
            <h4 className="mb-4">{isEdit ? "Update Company" : "Create Company"}</h4>

            {!!message && (
              <Alert color="danger" className="mb-4">
                {String(message)}
              </Alert>
            )}
            {uploadError && (
              <Alert color="danger" className="mb-4">
                {uploadError}
              </Alert>
            )}

            {isEdit && isPublicCompany && (
              <div className="mb-3">
                <span className="badge bg-info text-dark">Public Portal</span>
                <p className="text-muted small mb-0 mt-1">
                  This configuration applies to all public portal users.
                </p>
              </div>
            )}

            {isEdit && isDistributorCompany && (
              <div className="mb-3">
                <span className="badge bg-info text-dark">Distributors Portal</span>
                <p className="text-muted small mb-0 mt-1">
                  This configuration applies to all distributor portal users.
                </p>
              </div>
            )}

            <Row>
              <Col md={6} className="mb-3">
                <Label>Company Name</Label>
                <Input
                  type="text"
                  placeholder="Acme Inc."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Col>
              <Col md={6} className="mb-3">
                <Label>Domain</Label>
                <Input
                  type="text"
                  placeholder="example.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  readOnly={isSpecialCompany}
                  disabled={isSpecialCompany}
                />
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Label>Primary Color</Label>
                <div className="d-flex gap-2">
                  <Input
                    type="color"
                    value={primaryColor}
                    style={{ maxWidth: 50, minHeight: 40 }}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                  />
                  <Input
                    name="primaryColor"
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    placeholder="#06427a"
                  />
                </div>
              </Col>

              <Col md={6} className="mb-3">
                <Label>{isEdit ? "Replace Logo (optional)" : "Company Logo"}</Label>
                <Input type="file" accept={IMAGE_ACCEPT} onChange={onLogoChange} />
                {logoPreview && (
                  <div className="mt-2">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      style={{ maxHeight: 80 }}
                      onError={(e) => { e.currentTarget.alt = "Logo not available"; }}
                    />
                  </div>
                )}
              </Col>
            </Row>

            <PortalCustomizationForm
              dashboardConfig={dashboardConfig}
              setDashboardConfig={setDashboardConfig}
              sidebarConfig={sidebarConfig}
              setSidebarConfig={setSidebarConfig}
              onUploadMedia={uploadViaApi}
              portalCustomHtmlEnabled={portalCustomHtmlEnabled}
              setPortalCustomHtmlEnabled={setPortalCustomHtmlEnabled}
              portalCustomHtml={portalCustomHtml}
              setPortalCustomHtml={setPortalCustomHtml}
              canEditPortalHtmlOverride={isSuperadmin}
              customPages={customPages}
              setCustomPages={setCustomPages}
              companyDocuments={companyDocuments}
              companyDocumentsReady={companyDocumentsReady}
              companyDocumentsError={companyDocumentsError}
            />

            <div className="action-btn-container">
              <Button disabled={creating || updating || uploadingLogo || deleting} color="secondary" onClick={() => props.router.navigate("/companies")}>
                Cancel
              </Button>

              {isEdit && !isSpecialCompany && (
                <button
                  className="add-new-user decline"
                  disabled={creating || updating || uploadingLogo || deleting}
                  onClick={() =>
                    dispatch(deleteCompany(props.router.navigate, getCompanies, companyId))
                  }
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              )}

              <button
                className="add-new-user"
                onClick={onSubmit}
                disabled={creating || updating || uploadingLogo || deleting}
                title={uploadingLogo ? "Uploading…" : ""}
              >
                {updating
                  ? "Updating…"
                  : creating
                    ? "Creating…"
                    : uploadingLogo
                      ? "Uploading…"
                      : isEdit
                        ? "Update"
                        : "Create"}
              </button>
            </div>
          </Container>
        ) : (
          <div
            className="loading-container"
            style={{ width: "100%", height: "60dvh", display: "grid", placeItems: "center" }}
          >
            <Spinner />
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

Company.propTypes = {
  router: PropTypes.object,
};

export default withRouter(Company);
